import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { InvoiceFormTypes } from "models/formTypes";
import { useDebouncedCallback } from "use-debounce";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { HTMLConstants, InvoiceConstants } from "utils/constants";
import { CreateInvoiceTemplatePreviewProps } from "models/propTypes/invoicePropTypes";
import { ImageMeta } from "models/propTypes/fileViewerPropTypes";
//#endregion
//#region components
import { FileViewerContainer } from "components/common";
import { Portal } from "@mui/material";
import { useInvoiceService, useRecurringInvoiceService } from "serviceHooks";
import { UtilsHelper } from "utils/helpers";
//#endregion

const InvoiceCreateTemplatePreview: React.FC<CreateInvoiceTemplatePreviewProps> = (props) => {
  const invoiceService = useInvoiceService();
  const recurringInvoiceService = useRecurringInvoiceService();

  const { values, additionalImages } = props;

  const invoiceTemplateMount = document.getElementById(HTMLConstants.EntityCreationLayoutLowerLeftPortalId);

  const [fileMeta, setFileMeta] = React.useState<ImageMeta[]>();
  const iframeRef = React.useRef<HTMLIFrameElement>(null);

  const hideDeliveryDate = useSelector((store: AppState) => store.auth.selectedCompany.hideInvoiceDeliveryDate);
  const noVatCompany = useSelector((store: AppState) => store.auth.selectedCompany.noVat);

  const loadInvoiceTemplate = useDebouncedCallback((values: InvoiceFormTypes.Create) => {
    const invoicePost: WintSuperkollAPIModel.InvoicePreviewRequest = props.isRecurring
      ? recurringInvoiceService.convertFormToPreviewRequest(
          values,
          noVatCompany,
          hideDeliveryDate,
          props.invoice as WintSuperkollAPIModel.RecurringInvoice
        )
      : invoiceService.convertFormToPreviewRequest(
          values,
          noVatCompany,
          hideDeliveryDate,
          props.invoice as WintSuperkollAPIModel.Invoice
        );

    if (
      props.invoice &&
      "serialNumber" in props.invoice &&
      !(props.invoice.quotationSerialNumber && props.invoice.quotationSerialNumber > 0)
    ) {
      invoicePost.serialNumber = props.invoice?.serialNumber;
      invoicePost.oCR = props.invoice?.ocr;
      invoicePost.oCRText = props.invoice?.ocrText;
    }
    invoiceService
      .generateInvoicePreview(invoicePost)
      .then((res) => {
        const htmlContent = res.data as unknown as string;

        // An empty preview never reaches the onload below, which skips an empty srcdoc.
        if (!htmlContent) {
          setFileMeta(additionalImages ?? []);
          return;
        }

        if (iframeRef.current) {
          iframeRef.current.srcdoc = htmlContent;

          iframeRef.current.onload = () => {
            if (!iframeRef.current?.srcdoc) return;
            const iframeDoc = iframeRef.current?.contentDocument || iframeRef.current?.contentWindow?.document;
            if (iframeDoc) {
              const pageCount = UtilsHelper.getMultiHtmlPageCount(iframeDoc);
              const templateMeta: ImageMeta[] = htmlContent
                ? [
                    {
                      id: 0,
                      contentType: "html",
                      numberOfPages: pageCount,
                      data: htmlContent,
                    },
                  ]
                : [];

              setFileMeta(templateMeta.concat(additionalImages ?? []));
              iframeRef.current.srcdoc = "";
            }
          };
        }
      })
      // A list, even an empty one, ends the loading state, so a failed preview shows the no-preview placeholder.
      .catch(() => setFileMeta(additionalImages ?? []));
  }, InvoiceConstants.DebounceDelay);

  React.useEffect(() => {
    loadInvoiceTemplate(values);
  }, [values, additionalImages]);

  return (
    <Portal container={invoiceTemplateMount}>
      <iframe title="invoicePreviewPlaceholder" ref={iframeRef} style={{ display: "none" }} />
      {/* Until the first preview is generated there is no file; without loading the viewer shows the no-preview placeholder. */}
      <FileViewerContainer meta={fileMeta} parentId={0} preventDownload loading={!fileMeta} />
    </Portal>
  );
};

export default InvoiceCreateTemplatePreview;
